import { useState } from "react";
import { toast } from "sonner";
import { X, Plus, Trash2, Loader2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { CONNECTOR_META, ACTIONS, emptyTask } from "./shared";

export default function NewJobModal({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [requestor, setRequestor] = useState("");
  const [slaHours, setSlaHours] = useState(24);
  const [slaPolicy, setSlaPolicy] = useState("ticket");
  const [tasks, setTasks] = useState([emptyTask()]);
  const [busy, setBusy] = useState(false);

  const setTask = (i, k, v) => setTasks((ts) => ts.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)));
  const addRow = () => setTasks((ts) => [...ts, emptyTask()]);
  const delRow = (i) => setTasks((ts) => ts.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!title.trim()) { toast.error("Please add a job title."); return; }
    const clean = tasks.filter((t) => t.app_name.trim());
    setBusy(true);
    try {
      await api.post("/orchestration/jobs", { title, requestor, sla_hours: Number(slaHours) || 24, sla_policy: slaPolicy, tasks: clean });
      toast.success("Provisioning job created.");
      onCreated();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="new-job-modal">
      <div className="bg-[var(--parchment)] rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="brand-band text-white px-6 py-4 flex items-center justify-between sticky top-0">
          <p className="font-semibold">New provisioning job</p>
          <button onClick={onClose} className="text-white/80 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-neutral-600">Job title</label>
              <input data-testid="job-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Onboard analyst — 3 apps" className="field-input mt-1" />
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-600">Requestor (who needs access)</label>
              <input data-testid="job-requestor-input" value={requestor} onChange={(e) => setRequestor(e.target.value)} placeholder="user@wemabank.com" className="field-input mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-neutral-600">SLA (hours)</label>
              <input type="number" min={1} value={slaHours} onChange={(e) => setSlaHours(e.target.value)} className="field-input mt-1" />
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-600">SLA clock starts</label>
              <select data-testid="sla-policy-select" value={slaPolicy} onChange={(e) => setSlaPolicy(e.target.value)} className="field-input mt-1">
                <option value="ticket">When ticket is raised (ITIL)</option>
                <option value="processing">When processing begins</option>
              </select>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-neutral-600">Application tasks</label>
              <button data-testid="add-task-row-btn" onClick={addRow} className="text-xs text-[var(--burgundy)] font-semibold flex items-center gap-1"><Plus className="w-3 h-3" /> Add app</button>
            </div>
            <div className="space-y-2 mt-2">
              {tasks.map((t, i) => (
                <div key={i} className="border border-[hsl(var(--border))] rounded-lg p-2.5" data-testid={`task-row-${i}`}>
                  <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto_auto] gap-2 items-center">
                    <input value={t.app_name} onChange={(e) => setTask(i, "app_name", e.target.value)} placeholder="Application name" className="field-input" data-testid={`task-app-${i}`} />
                    <select value={t.connector} onChange={(e) => setTask(i, "connector", e.target.value)} className="field-input" data-testid={`task-connector-${i}`}>
                      {Object.entries(CONNECTOR_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                    <select value={t.action} onChange={(e) => setTask(i, "action", e.target.value)} className="field-input !w-auto">
                      {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                    <button onClick={() => delRow(i)} className="text-neutral-400 hover:text-red-600 justify-self-end"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-2 items-center mt-2">
                    <input value={t.target} onChange={(e) => setTask(i, "target", e.target.value)} placeholder="Group / role / entitlement (e.g. maker, Teller-ReadOnly)" className="field-input text-xs" data-testid={`task-target-${i}`} />
                    <label className="text-xs text-neutral-600 inline-flex items-center gap-1.5 whitespace-nowrap">
                      <input type="checkbox" checked={t.requires_approval} onChange={(e) => setTask(i, "requires_approval", e.target.checked)} data-testid={`task-approval-${i}`} /> Approval
                    </label>
                    <label className="text-xs text-neutral-600 inline-flex items-center gap-1.5 whitespace-nowrap">
                      <input type="checkbox" checked={t.requires_mfa} onChange={(e) => setTask(i, "requires_mfa", e.target.checked)} data-testid={`task-mfa-${i}`} /> Step-up MFA
                    </label>
                  </div>
                  {t.requires_approval && (
                    <input value={t.approver} onChange={(e) => setTask(i, "approver", e.target.value)} placeholder="Approver (e.g. line-manager)" className="field-input text-xs mt-2" data-testid={`task-approver-${i}`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="btn btn-outline">Cancel</button>
            <button data-testid="create-job-submit" onClick={submit} disabled={busy} className="btn btn-primary">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create job"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
